<template>
  <div class="face-building-game">
    <div v-if="!isGameComplete">
      <!-- Only display game content when the game is not complete -->
      <div v-if="targetFacePart">
        <!-- Face area where parts light up -->
        <div class="face-container">
          <!-- Face outline -->
          <div class="face-outline" />
          <!-- Hair -->
          <div
            v-if="completedFaceParts.includes('Hair')"
            class="face-part hair"
          >
            <!-- Use an image if you have one, otherwise use the emoji -->
            <img
              v-if="hairImageUrl"
              :src="hairImageUrl"
              alt="Hair"
            >
            <span v-else>🦱</span>
          </div>
          <!-- Eyes -->
          <div
            v-if="completedFaceParts.includes('Eyes')"
            class="face-part eyes"
          >
            👀
          </div>
          <!-- Left Ear -->
          <div
            v-if="completedFaceParts.includes('Ears')"
            class="face-part left-ear"
          >
            👂
          </div>
          <!-- Right Ear -->
          <div
            v-if="completedFaceParts.includes('Ears')"
            class="face-part right-ear"
          >
            👂
          </div>
          <!-- Nose -->
          <div
            v-if="completedFaceParts.includes('Nose')"
            class="face-part nose"
          >
            👃
          </div>
          <!-- Mouth -->
          <div
            v-if="completedFaceParts.includes('Mouth')"
            class="face-part mouth"
          >
            👄
          </div>
        </div>

        <h1 class="target-face-part">
          Tap the
          <span>{{ targetFacePart.name }}</span>
          !
          <span class="face-part-emoji">{{ targetFacePart.emoji }}</span>
        </h1>

        <div class="game-area">
          <div
            v-for="(part, index) in displayedParts"
            :key="index"
            :class="['face-part-item', { tapped: part.tapped }]"
            :style="{ top: `${part.y}%`, left: `${part.x}%` }"
            @click="tapFacePart(index)"
          >
            <span class="emoji">{{ part.emoji }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <!-- Display the game completion message when the game is complete -->
      <div class="game-complete">
        <p>🎉 Excellent!👏</p>
        <!-- Display the completed face -->
        <div class="face-container">
          <!-- Face outline -->
          <div class="face-outline" />
          <!-- Hair -->
          <div class="face-part hair">
            <!-- Use an image if you have one, otherwise use the emoji -->
            <img
              v-if="hairImageUrl"
              :src="hairImageUrl"
              alt="Hair"
            >
            <span v-else>🦱</span>
          </div>
          <!-- Eyes -->
          <div class="face-part eyes">
            👀
          </div>
          <!-- Left Ear -->
          <div class="face-part left-ear">
            👂
          </div>
          <!-- Right Ear -->
          <div class="face-part right-ear">
            👂
          </div>
          <!-- Nose -->
          <div class="face-part nose">
            👃
          </div>
          <!-- Mouth -->
          <div class="face-part mouth">
            👄
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  mixins: [speechMixin],
  data() {
    return {
      hairImageUrl:
        "https://i.pinimg.com/originals/9c/db/43/9cdb435823e315ad4767e0c8e424b8f3.png", // Replace with your image URL if you have one, else leave as null
      allFaceParts: [
        { name: "Hair", emoji: "🦱" },
        { name: "Eyes", emoji: "👀" },
        { name: "Ears", emoji: "👂" },
        { name: "Nose", emoji: "👃" },
        { name: "Mouth", emoji: "👄" },
      ],
      facePartsToPlay: [],
      targetFacePart: null,
      displayedParts: [],
      completedFacePartsCount: 0,
      completedFaceParts: [], // Array to track completed face parts
      isGameComplete: false,
    };
  },
  mounted() {
    this.startGame();
  },
  methods: {
    startGame() {
      // Clone the allFaceParts array to facePartsToPlay
      this.facePartsToPlay = [...this.allFaceParts];
      this.completedFacePartsCount = 0;
      this.completedFaceParts = []; // Reset completed face parts
      this.isGameComplete = false;
      this.nextFacePart();
    },
    nextFacePart() {
      if (this.facePartsToPlay.length === 0) {
        // Game complete
        this.isGameComplete = true;
        this.speakOutLoud("Excellent!");
        setTimeout(() => {
          this.$router.push("/body");
        }, 4000);
        return;
      }

      // Select a random face part from the list
      const randomIndex = Math.floor(
        Math.random() * this.facePartsToPlay.length
      );
      this.targetFacePart = this.facePartsToPlay.splice(randomIndex, 1)[0];

      // Prepare displayed parts
      this.displayedParts = [];

      // Add the target face part
      this.displayedParts.push({
        ...this.targetFacePart,
        x: Math.random() * 80,
        y: Math.random() * 80,
        tapped: false,
      });

      // Add other random face parts
      const nonTargetParts = this.allFaceParts.filter(
        (part) => part.name !== this.targetFacePart.name
      );
      const shuffledNonTargetParts = this.shuffleArray(nonTargetParts).slice(
        0,
        5
      ); // Show 5 other face parts
      shuffledNonTargetParts.forEach((part) => {
        this.displayedParts.push({
          ...part,
          x: Math.random() * 80,
          y: Math.random() * 80,
          tapped: false,
        });
      });

      // Shuffle displayed parts
      this.displayedParts = this.shuffleArray(this.displayedParts);

      // Speak the target face part
      this.speakOutLoud(`Tap the ${this.targetFacePart.name}!`);
    },
    shuffleArray(array) {
      // Fisher-Yates shuffle
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    tapFacePart(index) {
      const part = this.displayedParts[index];
      if (part.tapped) return;

      if (part.name === this.targetFacePart.name) {
        // Correct face part
        part.tapped = true;

        // Play success sound
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );

        // Speak the face part name
        this.speakOutLoud(part.name);

        // Update progress
        this.completedFacePartsCount++;
        this.completedFaceParts.push(this.targetFacePart.name); // Add to completed face parts

        // Proceed to next face part after a delay
        setTimeout(() => {
          this.nextFacePart();
        }, 1000);
      } else {
        // Incorrect face part
        // Play error sound (Updated URL)
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-button.mp3"
        );
      }
    },
    speakOutLoud(text) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.pitch = 1.2;
      utterance.rate = 0.7;
      window.speechSynthesis.speak(utterance);
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play().catch((e) =>
        console.error(`Error playing sound from ${url}:`, e)
      );
    },
  },
};
</script>

<style scoped>
.face-building-game {
  text-align: center;
}

/* Face container styles */
.face-container {
  position: relative;
  width: 200px;
  height: 250px;
  margin: 0 auto 20px;
}

.face-outline {
  width: 100%;
  height: 100%;
  background-color: #ffe0bd;
  border-radius: 100px;
  position: relative;
}

/* Face parts positioning */
.face-part {
  position: absolute;
}

.face-part.hair {
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 3rem;
}

.face-part.hair img {
  width: 250px;
  height: auto;
  z-index: 100;
}

.face-part.eyes {
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 4rem;
  z-index: 100;
}

/* Left Ear */
.face-part.left-ear {
  top: 80px;
  left: -25px;
  font-size: 4rem;
  transform: scaleX(-1);
}

/* Right Ear */
.face-part.right-ear {
  top: 80px;
  right: -25px;
  font-size: 4rem;
}

.face-part.nose {
  top: 90px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 4rem;
}

.face-part.mouth {
  top: 150px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 4rem;
}

.target-face-part {
  font-size: 2rem;
  margin-bottom: 10px;
}

.face-part-emoji {
  font-size: 2rem;
  margin-left: 10px;
}

.game-area {
  position: relative;
  height: 500px;
  border: 2px solid #ccc;
  overflow: hidden;
  margin: 0 auto;
  width: 80%;
  margin-bottom: 30px;
  background: linear-gradient(to bottom, #87ceeb 0%, #ffffff 100%);
}

.face-part-item {
  position: absolute;
  width: 60px;
  height: 60px;
  cursor: pointer;
}

.face-part-item .emoji {
  font-size: 3rem;
}

.face-part-item.tapped {
  visibility: hidden;
}

.game-complete {
  font-size: 2rem;
  color: green;
}

.game-complete .face-container {
  margin-top: 20px;
}
</style>